import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { 
  CreditCard, 
  Plus, 
  Search, 
  RefreshCw,
  Edit,
  Trash2,
  Users,
  Calendar,
  DollarSign,
  TrendingUp,
  Crown,
  Star,
  Zap,
  X,
  Check,
  Clock
} from 'lucide-react';
import './Subscriptions.css';

function Subscriptions() {
  const [plans, setPlans] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [stats, setStats] = useState({
    total_subscribers: 0,
    active_subscriptions: 0,
    revenue_month: 0,
    growth: 0
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('plans');
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    duration_days: 30,
    features: '',
    is_active: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [plansRes, subsRes, statsRes] = await Promise.all([
        api.get('/api/admin/subscriptions/plans').catch(() => ({ data: { plans: [] } })),
        api.get('/api/admin/subscriptions/subscribers').catch(() => ({ data: { subscribers: [] } })),
        api.get('/api/admin/subscriptions/stats').catch(() => ({ data: { stats: {} } }))
      ]);

      setPlans(plansRes.data.plans || []);
      setSubscribers(subsRes.data.subscribers || []);
      if (statsRes.data.stats) {
        setStats(prev => ({ ...prev, ...statsRes.data.stats }));
      }
    } catch (error) {
      console.error('Ошибка загрузки данных:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        features: formData.features.split('\n').filter(f => f.trim())
      };

      if (editingPlan) {
        await api.put(`/api/admin/subscriptions/plans/${editingPlan.id}`, data);
      } else {
        await api.post('/api/admin/subscriptions/plans', data);
      }
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Ошибка сохранения:', error);
      alert('Ошибка сохранения тарифа');
    }
  };

  const deletePlan = async (id) => {
    if (!window.confirm('Удалить этот тариф?')) return;
    try {
      await api.delete(`/api/admin/subscriptions/plans/${id}`);
      loadData();
    } catch (error) {
      console.error('Ошибка удаления:', error);
    }
  };

  const openEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name || '',
      description: plan.description || '',
      price: plan.price?.toString() || '',
      duration_days: plan.duration_days || 30,
      features: Array.isArray(plan.features) ? plan.features.join('\n') : '',
      is_active: plan.is_active !== false
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingPlan(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      duration_days: 30,
      features: '',
      is_active: true
    });
  };

  const getPlanIcon = (name) => {
    const lower = name?.toLowerCase() || '';
    if (lower.includes('premium') || lower.includes('про')) return Crown;
    if (lower.includes('pro') || lower.includes('бизнес')) return Zap;
    return Star;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('ru-RU');
  };

  const filteredSubscribers = subscribers.filter(sub => 
    sub.username?.toLowerCase().includes(search.toLowerCase()) ||
    sub.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <span>Загрузка...</span>
      </div>
    );
  }

  return (
    <div className="subscriptions-page fade-in">
      <div className="page-header">
        <h1 className="page-title">Подписки</h1>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={loadData}>
            <RefreshCw size={18} />
            Обновить
          </button>
          <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
            <Plus size={18} />
            Новый тариф
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon subscribers">
            <Users size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.total_subscribers}</span>
            <span className="stat-label">Всего подписчиков</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon active">
            <Check size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.active_subscriptions}</span>
            <span className="stat-label">Активных подписок</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon revenue">
            <DollarSign size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">${stats.revenue_month}</span>
            <span className="stat-label">Доход за месяц</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon growth">
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">+{stats.growth}%</span>
            <span className="stat-label">Рост</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <button 
          className={`tab ${activeTab === 'plans' ? 'active' : ''}`}
          onClick={() => setActiveTab('plans')}
        >
          <CreditCard size={18} />
          Тарифы
        </button>
        <button 
          className={`tab ${activeTab === 'subscribers' ? 'active' : ''}`}
          onClick={() => setActiveTab('subscribers')}
        >
          <Users size={18} />
          Подписчики
        </button>
      </div>

      {/* Plans Tab */}
      {activeTab === 'plans' && (
        <div className="plans-grid">
          {plans.length === 0 ? (
            <div className="empty-state">
              <CreditCard size={48} />
              <h3>Нет тарифов</h3>
              <p>Создайте первый тариф подписки</p>
            </div>
          ) : (
            plans.map(plan => {
              const Icon = getPlanIcon(plan.name);
              return (
                <div key={plan.id} className={`plan-card ${!plan.is_active ? 'inactive' : ''}`}>
                  <div className="plan-header">
                    <div className="plan-icon">
                      <Icon size={24} />
                    </div>
                    <div className="plan-actions">
                      <button className="btn btn-ghost btn-sm" onClick={() => openEdit(plan)}>
                        <Edit size={16} />
                      </button>
                      <button className="btn btn-ghost btn-sm delete" onClick={() => deletePlan(plan.id)}>
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  <h3 className="plan-name">{plan.name}</h3>
                  <p className="plan-description">{plan.description}</p>

                  <div className="plan-price">
                    <span className="price-value">${plan.price}</span>
                    <span className="price-period">/ {plan.duration_days} дней</span>
                  </div>

                  {plan.features && plan.features.length > 0 && (
                    <ul className="plan-features">
                      {(Array.isArray(plan.features) ? plan.features : []).map((feature, idx) => (
                        <li key={idx}>
                          <Check size={14} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="plan-footer">
                    <span className={`status-badge ${plan.is_active ? 'active' : 'inactive'}`}>
                      {plan.is_active ? 'Активен' : 'Неактивен'}
                    </span>
                    <span className="subscribers-count">
                      <Users size={14} />
                      {plan.subscribers_count || 0}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Subscribers Tab */}
      {activeTab === 'subscribers' && (
        <div className="subscribers-section">
          <div className="search-box">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              className="input"
              placeholder="Поиск подписчиков..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {filteredSubscribers.length === 0 ? (
            <div className="empty-state">
              <Users size={48} />
              <h3>Нет подписчиков</h3>
              <p>Подписчики появятся здесь</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Пользователь</th>
                    <th>Тариф</th>
                    <th>Статус</th>
                    <th>Дата начала</th>
                    <th>Дата окончания</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubscribers.map(sub => (
                    <tr key={sub.id}>
                      <td>
                        <div className="user-cell">
                          <div className="user-avatar">
                            {sub.username?.[0]?.toUpperCase() || 'U'}
                          </div>
                          <div className="user-info">
                            <span className="user-name">{sub.username || 'User'}</span>
                            <span className="user-email">{sub.email}</span>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="plan-badge">{sub.plan_name}</span>
                      </td>
                      <td>
                        <span className={`status-badge ${sub.is_active ? 'active' : 'expired'}`}>
                          {sub.is_active ? 'Активна' : 'Истекла'}
                        </span>
                      </td>
                      <td>{formatDate(sub.start_date)}</td>
                      <td>
                        <div className="end-date">
                          <Clock size={14} />
                          {formatDate(sub.end_date)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingPlan ? 'Редактировать тариф' : 'Новый тариф'}
              </h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="input-group">
                  <label className="input-label">Название</label>
                  <input
                    type="text"
                    className="input"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Premium"
                    required
                  />
                </div>

                <div className="input-group">
                  <label className="input-label">Описание</label>
                  <textarea
                    className="input textarea"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Описание тарифа..."
                    rows={2}
                  />
                </div>

                <div className="input-row">
                  <div className="input-group">
                    <label className="input-label">Цена ($)</label>
                    <input
                      type="number"
                      className="input"
                      value={formData.price}
                      onChange={(e) => setFormData({...formData, price: e.target.value})}
                      placeholder="9.99"
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>

                  <div className="input-group">
                    <label className="input-label">Длительность (дней)</label>
                    <input
                      type="number"
                      className="input"
                      value={formData.duration_days}
                      onChange={(e) => setFormData({...formData, duration_days: parseInt(e.target.value)})}
                      min="1"
                      required
                    />
                  </div>
                </div>

                <div className="input-group">
                  <label className="input-label">Функции (каждая с новой строки)</label>
                  <textarea
                    className="input textarea"
                    value={formData.features}
                    onChange={(e) => setFormData({...formData, features: e.target.value})}
                    placeholder="Без рекламы&#10;Приоритетная поддержка&#10;Эксклюзивный контент"
                    rows={4}
                  />
                </div>

                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  />
                  <span className="toggle-switch"></span>
                  <span>Тариф активен</span>
                </label>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>
                  Отмена
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingPlan ? 'Сохранить' : 'Создать'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Subscriptions;
